<script>
  import { getIconHtml } from "../utils/icons";

  import ImageCarousel from "./ImageCarousel.vue";
  import HoursPopover from "./HoursPopover.vue";
  import _ from "lodash";
  import Event from "./Event.vue";

  export default {
    components: { ImageCarousel, HoursPopover, Event },
    name: "BusinessCard",

    props: {
      business: {
        required: true,
        type: Object,
        validator: businessValidator,
      },
      isSelected: {
        required: false,
        type: Boolean,
        default: false,
      },
      closureMessage: {
        required: true,
        type: String,
      },
      pendingOpeningMessage: {
        required: true,
        type: String,
      },
      isLocatorEmbeded: {
        require: true,
        type: Boolean,
      },
      businessCardShowcaseFlags: {
        required: true,
        type: Object,
      },
      businessCardShowDetailIcons: {
        required: true,
        type: Boolean,
      },
      iconLibrary: {
        required: true,
        type: String,
      },
    },
    data() {
      return {
        imgCarouselVisible: false,
      };
    },
    methods: {
      showBusinessServicesList(business) {
        return (
          this.businessCardShowcaseFlags.show_services_list &&
          business.services_list.length > 0
        );
      },
      getBusinessUrl(business) {
        if (business.business_link) {
          return business.business_link;
        } else if (business.non_storefront_url) {
          return business.non_storefront_url;
        }
        return "";
      },
      getIconHtml,
    },
    computed: {
      showBusinessAttributes() {
        // Only display business attributes if global config store locator show services list is false
        return !this.businessCardShowcaseFlags.show_services_list;
      },
      locationAddress() {
        return this.business.address_text_lines.join(", ");
      },
      locationImages() {
        return this.business.get_images_medialinks.map(
          ({ uri, alt_text, caption }) => ({
            uri: uri,
            alt: alt_text
              ? alt_text
              : `Picture of ${this.business.display_name}`,
            caption,
          }),
        );
      },
      showDistance() {
        // Distance will be shown as default or if user creates a user store locator config
        // and uncheck display_distance flag
        return (
          this.business.get_storefront_config.display_distance ||
          _.isEmpty(this.business.get_storefront_config)
        );
      },
      showBusinessCardImage() {
        return (
          (this.business.get_storefront_config.display_business_card_image ||
            _.isEmpty(this.business.get_storefront_config)) &&
          this.business.main_image_uri_medialink
        );
      },
      roundedDistance() {
        return parseFloat(this.business.distance).toFixed(1);
      },
      htmlHours() {
        // stringHrs is a list of lists for the days of the week, where the first day is today.
        // Ex: [[[<today-weekday>, <htmlhours>]], [[<tomorrow-weekday>, <htmlhours>]],...]
        // See lmbrowser.models.Business._build_hours_list
        const todaysOpenData = this.business?.all_opening_hours?.stringHrs[0];
        // Use the first entry for today's data, retuned as [[<weekday>, <htmlhours>]], and extract just the HTML hours.
        if (todaysOpenData) {
          return todaysOpenData[0][1];
        }
        return null;
      },
      todayHours() {
        const todaysOpenData = this.business?.all_opening_hours?.stringHrs[0];
        if (!todaysOpenData) return [];
        return todaysOpenData.map((h) => h[1]);
      },
      openStatusText() {
        if (this.business.all_opening_hours.isOpen === null) {
          return "";
        } else {
          return this.business.all_opening_hours.isOpen ? "Open" : "Open today";
        }
      },
      featuredButtonDisplay() {
        // featured button will be shown by default or when creating a new
        // storelocatorconfig object. User have the ability to turn off the
        // featured button in storelocatorconfig
        // backend will return the appropriate link (BD or PL pge)
        if (
          "featured_button_link" in this.business.get_storefront_config &&
          this.business.get_storefront_config.featured_button_link
        ) {
          return "CUSTOM";
        } else if (
          // no storelocatorconfig found, default to in-stock products button
          _.isEmpty(this.business.get_storefront_config) &&
          (this.business.business_link || this.business.non_storefront_url)
        ) {
          return "DEFAULT";
        }
        return undefined;
      },
      businessInfoDisplayed() {
        if (
          this.businessCardShowcaseFlags.show_address ||
          this.businessCardShowcaseFlags.show_phone_number ||
          this.businessCardShowcaseFlags.show_open_status_and_hours
        ) {
          return true;
        }
        return false;
      },
    },
  };
  function businessValidator(obj) {
    const expectedFields = [
      "display_name",
      "lat",
      "lon",
      "distance",
      "address_text_lines",
      "all_opening_hours",
      "get_directions_link",
      "business_link",
      "business_attributes_showcase",
      "contact_context",
      "business_type_text",
      "non_storefront_url",
    ];
    return expectedFields.every((field) => field in obj);
  }
</script>

<template>
  <div
    @click="$emit('businessCardClick')"
    class="business-card"
    :class="{ selected: isSelected }"
    data-visual-test="locator-business-list-biz-card"
  >
    <div>
      <div
        class="business-header__text"
        :class="{
          'b-primary': business.business_type_css_treatment === '1',
          'b-secondary': business.business_type_css_treatment === '2',
          'b-tertiary': business.business_type_css_treatment === '3',
        }"
      >
        <span
          v-if="business.business_type_highlight_text"
          class="business-header__highlight-text"
        >
          {{ business.business_type_highlight_text }}
        </span>
        <div class="business-header__meta">
          <span v-if="business.business_type_text" class="store-type">
            {{ business.business_type_text }}
          </span>
          <span class="distance" v-if="showDistance">
            {{ roundedDistance }} mi
          </span>
        </div>
      </div>
      <div class="business-header">
        <p class="business-header__display-name">{{ business.display_name }}</p>
        <div
          v-if="business.get_storefront_config.display_business_group"
          class="business-card__business-group-name"
        >
          {{ business.business_group.display_name }}
        </div>
        <div class="business-header__business-main">
          <!-- eslint-disable-next-line vue/no-deprecated-v-bind-sync -->
          <el-dialog :visible.sync="imgCarouselVisible" width="95%">
            <image-carousel :images="locationImages" />
          </el-dialog>
          <button
            class="business-card__photo"
            @click="imgCarouselVisible = !imgCarouselVisible"
            v-if="showBusinessCardImage"
            data-dl2="Locator-Image-Carousel"
            data-dl2-e201="View-Image"
            data-dl2-e210="Business"
            :data-dl2-e211="business.dl2_click_id"
          >
            <img
              :src="business.main_image_uri_medialink"
              :alt="'Image gallery for ' + business.display_name"
            />
          </button>
          <div
            class="business-card__attributes"
            v-if="
              showBusinessAttributes &&
              business.business_attributes_showcase &&
              business.business_attributes_showcase.length > 0
            "
          >
            <span
              v-for="attribute in business.business_attributes_showcase"
              :key="attribute.url_name"
            >
              {{ attribute.display_name }}
            </span>
          </div>
          <div
            class="business-card__services"
            v-else-if="showBusinessServicesList(business)"
          >
            <div
              v-if="businessCardShowcaseFlags.store_locator_services_list_title"
              class="business-card__services__header"
            >
              {{ businessCardShowcaseFlags.store_locator_services_list_title }}
            </div>
            <div class="business-card__services__list">
              <div v-for="service in business.services_list" :key="service">
                {{ service }}
              </div>
            </div>
          </div>
        </div>
        <event
          v-if="Object.keys(business.events_showcase).length"
          :eventsShowcase="business.events_showcase"
          :businessLink="business.business_link"
        ></event>
      </div>
      <hr :class="[businessInfoDisplayed ? 'margin-15' : 'margin-10']" />
      <div class="business-card__business-info">
        <div v-if="business.get_storefront_config.description">
          {{ business.get_storefront_config.description }}
        </div>
        <div class="font-size-14" v-if="business.stock_indicator">
          <strong :class="business.stock_indicator.stock_img">
            {{ business.stock_indicator.stock_description }},
          </strong>
          {{ business.stock_indicator.stock_details }}
        </div>
        <template
          v-if="!business.permanently_closed && !business.pending_opening"
        >
          <div
            :class="{
              'business-card__data-line':
                businessCardShowcaseFlags.show_open_status_and_hours,
            }"
          >
            <div
              v-if="businessCardShowDetailIcons"
              v-html="getIconHtml('time', iconLibrary)"
              class="vsl-icon icon-detail"
            ></div>
            <!-- Location open status/hours -->
            <hours-popover
              :business="business"
              v-if="businessCardShowcaseFlags.show_open_status_and_hours"
            >
              <template v-slot:hour>
                <el-button class="hours-btn" v-if="htmlHours == 'Closed'">
                  <strong
                    class="business-card__hours__status"
                    v-html="htmlHours"
                  ></strong>
                </el-button>
                <el-button
                  v-else-if="htmlHours == 'Temporarily Closed'"
                  class="business-card__hours hours-btn cs-closure"
                >
                  <div
                    v-html="getIconHtml('warning', iconLibrary)"
                    class="temp-closed-warning-icon"
                  ></div>
                  <span v-html="htmlHours"></span>
                </el-button>
                <div v-else class="business-card__hours hours-btn">
                  <!-- day -->
                  <div>
                    <strong class="business-card__hours__status open">
                      {{ openStatusText }}
                    </strong>
                  </div>
                  <!-- hours -->
                  <div>
                    <div
                      v-for="(hour, idx) in todayHours"
                      :key="idx"
                      v-html="hour"
                      class="cs-html-hours"
                    ></div>
                  </div>
                </div>
              </template>
            </hours-popover>
          </div>
          <!-- Location address link -->
          <div
            v-if="
              business.address_text_lines.length &&
              businessCardShowcaseFlags.show_address
            "
            ref="address-text"
            class="business-card__data-line"
          >
            <div
              v-if="businessCardShowDetailIcons"
              v-html="getIconHtml('mapMarkerOutline', iconLibrary)"
              class="vsl-icon icon-detail"
            ></div>
            <a
              target="_blank"
              rel="noopener noreferrer"
              data-dl2="Locator-Card-Text-Get-Directions"
              data-dl2-e201="Get-Directions"
              data-dl2-e203="y"
              data-dl2-e210="Business"
              :data-dl2-e211="business.dl2_click_id"
              :aria-label="'Directions to ' + locationAddress"
              :href="business.get_directions_link"
              class="cs-location-address"
            >
              {{ locationAddress }}
            </a>
          </div>
          <div
            v-if="
              business.contact_context.business_phone_text &&
              businessCardShowcaseFlags.show_phone_number
            "
            class="business-card__data-line"
            ref="phone-text"
          >
            <div
              v-if="businessCardShowDetailIcons"
              v-html="getIconHtml('callOutline', iconLibrary)"
              class="vsl-icon icon-detail"
            ></div>
            <a
              :href="'tel:' + business.contact_context.business_phone_raw"
              rel="noopener noreferrer"
              data-dl2="Locator-Card-Text-Call"
              data-dl2-e201="Call"
              data-dl2-e203="y"
              data-dl2-e210="Business"
              :data-dl2-e211="business.dl2_click_id"
              :aria-label="
                'Call ' + business.contact_context.business_phone_text
              "
              class="cs-call"
            >
              <span
                v-if="business.phone_number_label"
                class="phone-number-label"
              >
                {{ business.phone_number_label }}:
              </span>
              {{ business.contact_context.business_phone_text }}
            </a>
          </div>
        </template>
        <div
          v-else-if="business.pending_opening && !business.permanently_closed"
          class="business-card__data-line"
        >
          <strong class="business-card__hours__status pending-opening">
            <div
              v-html="getIconHtml('warning', iconLibrary)"
              class="warning-icon"
            ></div>
            {{ pendingOpeningMessage }}
          </strong>
        </div>
        <div v-else class="business-card__data-line closed-data-line">
          <strong class="business-card__hours__status closed">
            <div
              v-html="getIconHtml('warning', iconLibrary)"
              class="warning-icon"
            ></div>
            <div>
              <div class="gc-link-font cs-closure">{{ closureMessage }}</div>
              <div class="cs-closure">{{ locationAddress }}</div>
            </div>
          </strong>
        </div>
        <!-- display address note here -->
        <div class="business-card__address-note" v-if="business.address_note">
          <span>
            {{ business.address_note }}
          </span>
        </div>
      </div>
    </div>
    <template v-if="!business.permanently_closed && !business.pending_opening">
      <div
        class="business-card__ctas ctas__top"
        v-if="business.get_storefront_buttons"
      >
        <template v-for="button in business.get_storefront_buttons">
          <div
            class="business-card__ctas__phone"
            :key="button.text"
            v-if="
              business.contact_context.business_phone_raw &&
              button.link_type === 'PHONE'
            "
          >
            <a
              :href="'tel:' + business.contact_context.business_phone_raw"
              rel="noopener noreferrer"
              data-dl2="Locator-Card-Button-Call"
              data-dl2-e201="Call"
              data-dl2-e203="y"
              data-dl2-e210="Business"
              :data-dl2-e211="business.dl2_click_id"
              :aria-label="`Call ${business.contact_context.business_phone_raw}`"
            >
              <div
                v-html="getIconHtml('call', iconLibrary)"
                class="vsl-icon icon-detail"
              ></div>
              <span class="business-card__ctas__text gc-link-font">
                {{ button.text }}
              </span>
            </a>
          </div>
          <div
            class="business-card__ctas__directions"
            :key="button.text"
            v-if="
              business.get_directions_link && button.link_type === 'DIRECTIONS'
            "
          >
            <a
              :href="business.get_directions_link"
              :target="button.open_in_new_tab ? '_blank' : false"
              rel="noopener noreferrer"
              data-dl2="Locator-Card-Button-Get-Directions"
              data-dl2-e201="Get-Directions"
              data-dl2-e203="y"
              data-dl2-e210="Business"
              :data-dl2-e211="business.dl2_click_id"
              :aria-label="'Directions to' + locationAddress"
            >
              <span
                class="business-card__direction-icon vsl-icon"
                aria-hidden="true"
                v-html="getIconHtml('direction', iconLibrary)"
              ></span>
              <span class="business-card__ctas__text gc-link-font">
                {{ button.text }}
              </span>
            </a>
          </div>
          <div
            class="business-card__ctas__appointment"
            :key="button.text"
            v-if="
              business.analytics_appointment_link &&
              button.link_type === 'APPOINTMENT'
            "
          >
            <a
              :href="business.analytics_appointment_link"
              data-dl2="Book-Appointment-Locator"
              data-dl2-e201="Book-Appointment"
              data-dl2-e203="y"
              data-dl2-e210="Business"
              :data-dl2-e211="business.dl2_click_id"
              :aria-label="`Book appointment at ${business.display_name} ${locationAddress}`"
            >
              <span
                class="business-card__book-online-icon vsl-icon"
                aria-hidden="true"
                v-html="getIconHtml('calendar', iconLibrary)"
              ></span>
              <span class="business-card__ctas__text gc-link-font">
                {{ button.text }}
              </span>
            </a>
          </div>
          <div
            class="business-card__ctas__location-detail"
            :key="button.text"
            v-if="
              getBusinessUrl(business) && button.link_type === 'LOCATION DETAIL'
            "
          >
            <a
              :href="getBusinessUrl(business)"
              data-dl2="Location-Detail"
              data-dl2-e201="Location-Detail"
              data-dl2-e203="y"
              data-dl2-e210="Business"
              :data-dl2-e211="business.dl2_click_id"
            >
              <span
                class="business-card__book-online-icon vsl-icon"
                aria-hidden="true"
                :class="button.css_icon"
              ></span>
              <span class="business-card__ctas__text gc-link-font">
                {{ button.text }}
              </span>
            </a>
          </div>
          <div
            class="business-card__ctas__custom"
            :key="button.text"
            v-if="button.link_type === 'CUSTOM'"
          >
            <a
              :href="button.url"
              :target="button.open_in_new_tab ? '_blank' : false"
              v-bind:[button.attribute_name_1]="button.attribute_value_1"
              v-bind:[button.attribute_name_2]="button.attribute_value_2"
              :aria-label="`${button.text} at ${locationAddress}`"
            >
              <span
                :class="button.css_icon"
                aria-hidden="true"
                class="fa-icon"
              ></span>
              <span class="business-card__ctas__text gc-link-font">
                {{ button.text }}
              </span>
            </a>
          </div>
        </template>
      </div>
      <div class="business-card__ctas ctas__top" v-else>
        <div
          v-if="business.contact_context.business_phone_raw"
          class="business-card__ctas__phone"
        >
          <a
            :href="'tel:' + business.contact_context.business_phone_raw"
            rel="noopener noreferrer"
            data-dl2="Locator-Card-Button-Call"
            data-dl2-e201="Call"
            data-dl2-e203="y"
            data-dl2-e210="Business"
            :data-dl2-e211="business.dl2_click_id"
            :aria-label="`Call ${business.contact_context.business_phone_raw}`"
          >
            <div
              v-html="getIconHtml('call', iconLibrary)"
              class="vsl-icon icon-detail"
            ></div>
            <span class="business-card__ctas__text gc-link-font">Call</span>
          </a>
        </div>
        <div
          v-if="business.get_directions_link"
          class="business-card__ctas__directions"
        >
          <a
            :href="business.get_directions_link"
            target="_blank"
            rel="noopener noreferrer"
            data-dl2="Locator-Card-Button-Get-Directions"
            data-dl2-e201="Get-Directions"
            data-dl2-e203="y"
            data-dl2-e210="Business"
            :data-dl2-e211="business.dl2_click_id"
            :aria-label="'Directions to ' + locationAddress"
          >
            <div
              v-html="getIconHtml('direction', iconLibrary)"
              class="vsl-icon icon-detail"
            ></div>
            <span class="business-card__ctas__text gc-link-font">
              Directions
            </span>
          </a>
        </div>
        <div
          v-if="business.analytics_appointment_link"
          class="business-card__ctas__appointment"
        >
          <a
            :href="business.analytics_appointment_link"
            data-dl2="Book-Appointment-Locator"
            data-dl2-e201="Book-Appointment"
            data-dl2-e203="y"
            data-dl2-e210="Business"
            :data-dl2-e211="business.dl2_click_id"
            :aria-label="`Book appointment at ${business.display_name} ${locationAddress}`"
          >
            <div
              v-html="getIconHtml('calendar', iconLibrary)"
              class="vsl-icon icon-detail"
            ></div>
            <span class="business-card__ctas__text gc-link-font">
              Appointment
            </span>
          </a>
        </div>
      </div>
      <div class="business-card__ctas">
        <a
          v-if="featuredButtonDisplay === 'CUSTOM'"
          :href="business.get_storefront_config.featured_button_link"
          target="_blank"
          class="featured-button gc-link-font"
          data-dl2="Locator-Featured-Button"
          data-dl2-e201="Custom-Button"
          data-dl2-e203="y"
          data-dl2-e210="Business"
          :data-dl2-e211="business.dl2_click_id"
          :aria-label="
            business.get_storefront_config.featured_button_text
              ? `${business.get_storefront_config.featured_button_text} ${business.display_name} at ${locationAddress}`
              : `View ${business.display_name} at ${locationAddress}`
          "
        >
          <span v-if="business.get_storefront_config.featured_button_text">
            {{ business.get_storefront_config.featured_button_text }}
          </span>
          <span v-else>View</span>
        </a>
        <a
          v-if="featuredButtonDisplay === 'DEFAULT' && getBusinessUrl(business)"
          :href="getBusinessUrl(business)"
          target="_blank"
          class="featured-button gc-link-font"
          data-dl2="Locator-Featured-Button"
          data-dl2-e201="Location-Link"
          data-dl2-e203="y"
          data-dl2-e210="Business"
          :data-dl2-e211="business.dl2_click_id"
          :aria-label="'Show Store Deals & Devices at ' + business.display_name"
        >
          <span>Show Store Deals & Devices</span>
        </a>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  @import "../styles/breakpoints";
  @import "../styles/variables";

  $selected-box-shadow: inset 0 0 0 2px $primary;

  .business-card {
    width: 95%;
    background: #ffffff;
    padding: 24px;
    margin: 0px 10px 16px;
    cursor: pointer;

    @include media(">=tablet") {
      padding: 24px 20px;
    }

    &.selected {
      box-shadow: $selected-box-shadow;
    }

    &__business-info {
      display: flex;
      flex-direction: column;
      font-size: 16px;
    }

    &__attributes {
      color: $primary-text;
      font-size: 12px;
      margin: 0 10px;
      & > span:before {
        content: "• ";
      }
    }
    &__services {
      color: $primary-text;
      font-size: 12px;
      &__header {
        margin: 0 10px;
        font-weight: 600;
      }
      &__list {
        margin: 0 10px 10px 20px;
        & > div:before {
          content: "• ";
        }
      }
    }
    &__hours {
      display: block;
      &__status {
        color: $primary;
        &.closed,
        &.pending-opening {
          color: #262626 !important;
          font-weight: 400;
          display: flex;
          gap: 8px;
          .warning-icon {
            color: #eed474;
          }
        }
      }
      &:has(.cs-html-hours) {
        display: flex;
        align-items: baseline;
        gap: 5px;
      }
      .cs-html-hours {
        margin-bottom: 5px;
      }
    }
    &__data-line,
    &__address-note {
      color: $primary-text;
      font-size: 14px;
      margin: 0 0 10px;
      &:hover,
      &:focus {
        span {
          text-decoration: underline;
          &.icon-detail {
            text-decoration: none !important;
          }
        }
      }
      > span {
        display: inline-block;
        font-weight: normal;
      }
      a {
        color: #262626;
      }
    }
    &__data-line {
      a {
        display: inline-block;
      }
    }
    &__data-line.closed-data-line {
      display: flex;
      flex-direction: column;
    }

    &__photo {
      position: relative;
      border: none;
      padding: 0px;
      background-color: inherit;
      img {
        width: 90px;
        height: 55px;
        object-fit: cover;
      }
      &:hover {
        cursor: zoom-in;
      }
    }
    &__closure-icon {
      width: 20px;
      vertical-align: bottom;
    }
    &__ctas {
      display: flex;
      flex-direction: row;
      justify-content: stretch;
      flex-grow: 1;
      margin: 14px 0;
      gap: 7px;
      & > * {
        flex: 1;
        border: 1px solid #dbdbdb;
        padding: 5px;
        text-align: center;
        &:hover {
          border: 1px solid $primary;
        }
      }
      a {
        text-decoration: none;
        text-align: center;
      }
      &__text {
        font-weight: bold;
        font-size: 12px;
        text-align: center;
        color: $primary-text;
        display: flex;
        flex-direction: column;
      }
      .vsl-icon {
        display: block;
        color: $primary;
        padding: 0.2rem 1.6rem;
      }
      .fa-icon:before {
        font-size: 20px;
      }
      .featured-button {
        font-weight: bold;
        font-size: 14px;
        text-align: center;
        padding: 12px 16px;
        color: $primary-text;
      }
    }
    &__business-group-name {
      color: #6a6a6a;
      margin-bottom: 0.8rem;
      font-size: 12px;
      text-transform: uppercase;
      margin-top: -0.5rem;
    }
  }
  .business-header {
    gap: 10px;
    display: flex;
    flex-direction: column;
    &__display-name {
      margin: 0;
      font-size: 20px;
      line-height: 22px;
      font-weight: 800;
    }
    &__highlight-text {
      // color in _vsl.scss
      font-weight: bold;
      display: block;
    }
    &__meta {
      display: flex;
      color: $secondary-text;
      font-size: 12px;
      text-transform: uppercase;
      justify-content: space-between;
      .distance {
        letter-spacing: 0.3px;
        white-space: nowrap;
        text-align: right;
        padding-left: 20px;
        flex: 1;
      }
    }

    &__business-main {
      display: flex;
      flex-direction: row;
    }
  }
  .fa-icon {
    font-family: "Font Awesome 5 Free";
    font-weight: 800;
    font-size: 24px;
    &::before {
      font-size: 24px;
    }
  }

  .address-icon {
    float: left;
  }
  :deep(*) {
    .el-dialog {
      box-shadow: none;
      background: rgba(black, 0.6);

      height: fit-content;
      &__wrapper {
        cursor: default;
        background: rgba(black, 0.4);
      }
      @include media(">=tablet") {
        width: 80% !important;
      }

      &__body {
        width: 100%;
        height: 80%;
      }
      .el-icon-close,
      .el-carousel__arrow {
        display: flex;
        justify-content: center;
        align-items: center;
        color: #fff;
        font-size: 20px;
        font-weight: bold;
      }
      &__close:hover {
        color: $primary !important;
      }
    }
  }
  .hours-btn {
    border: none;
    padding-left: 0;
    padding-bottom: 0;
    padding-top: 0;
    color: #262626;
    &:hover,
    &:focus {
      background-color: transparent;
      color: #262626;
    }
    .temp-closed-warning-icon {
      display: inline;
      vertical-align: middle;
      color: #eed474;
    }
  }
</style>

<style lang="scss">
  // global style
  .vsl-icon {
    display: inline-block;
    font-size: 24px;
    &.icon-detail {
      vertical-align: text-bottom;
      padding-right: 8px;
      i {
        width: 24px;
        text-align: center;
      }
    }
  }
  .ctas__top {
    .material-icons {
      vertical-align: sub !important;
    }
  }
  .font-size-14 {
    font-size: 14px;
    .lm-product__stock {
      font-size: 14px;
      font-weight: bold;
    }
  }
  .margin-15 {
    margin-top: 15px;
    margin-bottom: 15px;
  }
  .margin-10 {
    margin-top: 10px;
    margin-bottom: 10px;
  }
</style>
